import { forwardRef } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Typography from "@mui/material/Typography";
import { LuCircleAlert } from "react-icons/lu";
import { getCurrentStatus } from "store/editor";
import { useDarkMode } from "../useDarkMode";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CoverageWarningModal({
  open,
  onClose,
  onSubmit,
}) {
  const workflowStatus = useSelector(getCurrentStatus);
  const isDarkMode = useDarkMode();

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-labelledby="coverage-warning-title"
      sx={{
        "& .MuiPaper-root": {
          bgcolor: isDarkMode ? "#26282C" : "#FFFFFF",
          borderRadius: "24px",
          boxShadow: isDarkMode
            ? "0px 8px 24px rgba(0,0,0,0.4)"
            : "0px 8px 24px rgba(0,0,0,0.12)",
          maxWidth: "500px",
          width: "100%",
        },
      }}
    >
      <Box sx={{ pb: "44px", px: "36px", pt: "32px" }}>
        <Box
          aria-hidden="true"
          sx={{
            width: 56,
            height: 56,
            border: "1px solid #FCE7DE",
            borderRadius: "14px",
            bgcolor: isDarkMode ? "#4A3800" : "#FEF8F4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <LuCircleAlert size={24} color="#EA580C" />
        </Box>

        <Typography
          id="coverage-warning-title"
          component="h2"
          sx={{
            fontSize: "30px",
            fontWeight: 500,
            lineHeight: "36px",
            color: isDarkMode ? "#FFF" : "#111827",
            mb: 1.5,
          }}
        >
          Suspicious Activity Detected
        </Typography>

        <Typography
          sx={{
            fontSize: "17px",
            fontWeight: 500,
            lineHeight: 1.5,
            color: isDarkMode ? "#878D98" : "#717182",
            mb: 4,
          }}
        >
          {process.env.REACT_APP_INTEGRITY_FAIL_REASSIGN_ENABLED === "true" ? (
            <>
              Our system has detected a number of indicators of suspicious activity on
              this file. This has been referred to your administrator. Please contact
              your admin to verify your quota for this file.
            </>
          ) : (
            <>
              {workflowStatus === "First_Review" && (
                <>
                  Our system has detected indicators on this file that may suggest speaker tags
                  in the transcript may be inaccurate or incomplete. Please continue reviewing
                  the transcript carefully against the audio and ensure that every paragraph is
                  tagged to the correct speaker before submission.
                  <br />
                  <br />
                  Submitted files with unresolved concerns may be referred for admin review,
                  and confirmed issues may affect quality assessment and invoice eligibility.
                  <br />
                  <br />
                  If you believe this notification was triggered in error and are confident
                  that all speaker tags have been fully verified, proceed with the file as
                  normal. If follow-up is needed, Ops will contact you directly.
                </>
              )}

              {(workflowStatus === "QA_Review" || workflowStatus === "Compliance") && (
                <>
                  Our system has detected indicators on this file that may suggest the submitted
                  output may not meet expected quality standards. Please continue reviewing the
                  transcript carefully against the audio and correct any remaining issues before
                  submission.
                  <br />
                  <br />
                  Submitted files with unresolved concerns may be referred for admin review, and
                  confirmed issues may affect quality assessment and invoice eligibility.
                  <br />
                  <br />
                  If you believe this notification was triggered in error and are confident
                  that the file has been fully reviewed, proceed with the file as normal. If
                  follow-up is needed, Ops will contact you directly.
                </>
              )}
            </>
          )}
        </Typography>

        {/* Primary: Continue Editing */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleClose}
          sx={{
            bgcolor: "#0659F9",
            borderRadius: "9999px",
            color: "#FFF",
            fontSize: "18px",
            fontWeight: 500,
            height: "56px",
            textTransform: "none",
            mb: 1.5,
            "&:hover": {
              bgcolor: "#0041CC",
            },
          }}
        >
          Continue Editing
        </Button>

        {/* Secondary: Submit Anyway */}
        <Button
          variant="outlined"
          fullWidth
          onClick={handleSubmit}
          sx={{
            borderColor: isDarkMode ? "#4A4C50" : "#D0D5DD",
            borderRadius: "9999px",
            color: isDarkMode ? "#E0E0E0" : "#111827",
            fontSize: "18px",
            fontWeight: 500,
            height: "56px",
            textTransform: "none",

            "&:hover": {
              borderColor: isDarkMode ? "#5A5C60" : "#98A2B3",
              bgcolor: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              color: isDarkMode ? "#E0E0E0" : "#111827",
            },
          }}
        >
          Submit Anyway
        </Button>
      </Box>
    </Dialog>
  );
}
